import type { FormEvent } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import Logo from './Logo';

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-border-emphasis rounded-sm text-[0.875rem] pointer-coarse:text-base font-sans bg-surface-inset text-ink placeholder-ink-muted focus:outline-none focus:border-accent focus:shadow-[0_0_0_3px_rgba(200,150,62,0.1)] transition-[border-color,box-shadow] duration-150';

const LABEL_CLASS = 'block mb-1.5 font-medium text-[0.8125rem] text-ink-secondary';

export default function Onboarding() {
  const { session, refreshMe, signOut } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState(session?.user?.email ?? '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.createOrganization({
        name: name.trim(),
        identifier: identifier.trim(),
        email: email.trim(),
      });
      // Recarga /api/me para que los guards vean la organización recién creada
      // antes de navegar; si no, ProtectedRoute rebotaría de vuelta al onboarding.
      await refreshMe();
      navigate('/', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : 'No se pudo crear la organización. Intenta de nuevo.'
      );
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-inset px-4 py-10">
      <div className="w-full max-w-[420px]">
        {/* Brand */}
        <div className="mb-8 text-center">
          <Logo size={44} className="text-accent mx-auto mb-3" />
          <h2 className="font-serif text-[1.6rem] font-semibold text-accent tracking-tight">
            Legal Hermes
          </h2>
          <p className="text-ink-muted text-[0.8125rem] mt-1.5">Monitor de procesos judiciales</p>
        </div>

        {/* Card */}
        <div className="bg-surface-raised rounded-lg p-7 border border-border-emphasis shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
          <div className="mb-6">
            <h3 className="text-[1.0625rem] text-ink font-medium mb-1.5">Crea tu organización</h3>
            <p className="text-[0.8125rem] text-ink-muted leading-relaxed">
              Tus procesos y búsquedas se agrupan bajo una organización. Necesitamos estos datos
              antes de empezar.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label className={LABEL_CLASS} htmlFor="org-name">
                Nombre de la organización
              </label>
              <input
                id="org-name"
                type="text"
                className={INPUT_CLASS}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Despacho Jurídico Pérez"
                required
                maxLength={200}
                autoComplete="organization"
              />
            </div>

            <div className="mb-5">
              <label className={LABEL_CLASS} htmlFor="org-identifier">
                NIT o RUT
              </label>
              <input
                id="org-identifier"
                type="text"
                className={INPUT_CLASS}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="Ej. 900123456-7"
                required
                maxLength={50}
              />
            </div>

            <div className="mb-6">
              <label className={LABEL_CLASS} htmlFor="org-email">
                Correo de notificaciones
              </label>
              <input
                id="org-email"
                type="email"
                className={INPUT_CLASS}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={320}
                autoComplete="email"
              />
              <p className="text-[0.75rem] text-ink-muted mt-1.5 leading-relaxed">
                A esta dirección llegan las alertas cuando cambia algo en tus procesos.
              </p>
            </div>

            {error && (
              <p className="text-destructive text-[0.8125rem] mb-4 bg-destructive-subtle px-3 py-2 rounded-sm border border-destructive/20">
                {error}
              </p>
            )}

            <button
              type="submit"
              className="w-full px-6 py-2.5 bg-accent text-ink-inverse border-none rounded-sm text-[0.8125rem] font-semibold font-sans inline-flex items-center justify-center gap-2 cursor-pointer transition-[background-color,opacity] duration-150 hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear organización'}
            </button>
          </form>
        </div>

        <div className="mt-5 text-center">
          <button
            type="button"
            onClick={signOut}
            className="text-[0.75rem] text-ink-muted hover:text-ink-secondary transition-colors duration-150 bg-transparent border-none cursor-pointer"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}
